import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';

export const HANDLED_OUTSIDE_REASONS = [
  { value: 'changes_by_email', label: 'Changes sent by email / phone', hint: 'The client sent their changes outside the portal and you keyed them into BrightPay.' },
  { value: 'no_changes', label: 'No changes — same as last month', hint: 'E.g. a director-only payroll that never changes, run as-is.' },
  { value: 'other', label: 'Other', hint: 'Add a short note so the team knows what happened.' },
];

export const handledReasonLabel = (value) =>
  (HANDLED_OUTSIDE_REASONS.find(r => r.value === value) || {}).label || value;

// Records that a client's month was run without a portal submission. Takes them
// off Not Submitted and stops that month's reminder emails.
export default function HandledOutsideModal({ company, monthLabel, onSave, onClose }) {
  const [reason, setReason] = useState('changes_by_email');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEscapeKey(onClose);

  const submit = async () => {
    if (reason === 'other' && !note.trim()) {
      setError('Add a short note when the reason is "Other".');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ reason, note: note.trim() });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Handled outside the portal</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-4">
          <span className="font-medium text-gray-800 dark:text-gray-200">{company.company_name}</span> · {monthLabel}.
          {' '}This moves them off Not Submitted and stops this month's reminder emails. It doesn't touch their portal data.
        </p>

        <fieldset className="space-y-2">
          <legend className="sr-only">Reason</legend>
          {HANDLED_OUTSIDE_REASONS.map(r => (
            <label
              key={r.value}
              className={`flex items-start gap-3 rounded-md p-3 ring-1 cursor-pointer ${reason === r.value ? 'ring-brand-500 bg-brand-50/60 dark:bg-gray-700' : 'ring-gray-200 dark:ring-gray-600'}`}
            >
              <input
                type="radio"
                name="handled-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="mt-1 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{r.label}</span>
                <span className="block text-[13px] text-gray-500 dark:text-gray-400">{r.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <label className="block text-sm text-gray-600 dark:text-gray-400 mt-4 mb-1">
          Note {reason === 'other' ? '(required)' : '(optional)'}
        </label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          maxLength={500}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm px-3 py-2 focus:ring-brand-500 focus:border-brand-500"
          placeholder="e.g. Emailed changes 22 Sept"
        />

        {error && <p className="text-sm text-rose-600 mt-3">{error}</p>}

        <div className="flex justify-end space-x-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-md bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Mark as handled'}
          </button>
        </div>
      </div>
    </div>
  );
}
